import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Search, User, ShoppingCart, ChevronRight, Computer, Settings, ShoppingBag, Phone } from 'lucide-react';
import * as Types from '../../services/api/api.types';
import { apiService } from '../../services/api/instance';
import { useAuth } from '../../contexts/AuthContext';
import SearchBar from '../common/SearchBar';
import logger from '../../utils/logger';
import { formatCurrency } from '../../utils/currency.utils';

interface MobileNavigationProps {
    cartItemsCount: number;
    cartTotal: number;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({ cartItemsCount, cartTotal }) => {
    const { user, isAuthenticated } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [categories, setCategories] = useState<Types.CategoryFull[]>([]);
    const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
    const [showSearch, setShowSearch] = useState(false);
    const navigate = useNavigate();

    const formattedTotal = formatCurrency(cartTotal);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const data = await apiService.getCategories();
                setCategories(data);
            } catch (error) {
                logger.error('Error loading categories:', error);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleSearchComplete = (query: string) => {
        setIsOpen(false);
        setShowSearch(false);
        // Navigate to search results page
        navigate(`/tarsene?query=${encodeURIComponent(query.trim())}`);
    };

    const toggleCategory = (categoryId: number) => {
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(categoryId)) {
            newExpanded.delete(categoryId);
        } else {
            newExpanded.add(categoryId);
        }
        setExpandedCategories(newExpanded);
    };

    const getBgName = (names: Types.Name[]): string => {
        const bgName = names.find(name => name.language_code === 'bg');
        return bgName ? bgName.text : '';
    };

    // Categories to exclude from the dropdown menu
    const excludedCategories = [
        'Дигитален ваучер за подарък',
        'Разопаковани продукти',
        'Услуги',
        'Компютърни системи'
    ];

    const parentCategories = categories.filter(cat => {
        const categoryName = getBgName(cat.name);
        return cat.parent === 0 && cat.show && !excludedCategories.includes(categoryName);
    });

    const getChildCategories = (parentId: number) => {
        return categories.filter(cat => cat.parent === parentId && cat.show);
    };

    const getCategorySlugOrId = (category: Types.CategoryFull): string =>
        category.slug ?? category.id.toString();

    return (
        <>
            {/* Mobile Header */}
            <header className="lg:hidden sticky top-0 bg-white border-b border-gray-200 z-50">
                <div className="px-4 py-3">
                    {/* Top Row */}
                    <div className="flex items-center mb-2">
                        <div className="flex-1 flex justify-start">
                            <button
                                type="button"
                                onClick={() => setIsOpen(!isOpen)}
                                className="p-3 -ml-2 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-colors touch-manipulation"
                                aria-label="Toggle menu"
                            >
                                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                            </button>
                        </div>

                        <Link to="/" onClick={() => setIsOpen(false)} className="flex-shrink-0">
                            <img src={require('../../logo.png')} alt="PC Center" className="h-9" />
                        </Link>

                        <div className="flex-1 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setShowSearch(!showSearch)}
                                className="p-3 -mr-1 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-colors touch-manipulation"
                                aria-label="Toggle search"
                            >
                                <Search className="h-5 w-5" />
                            </button>
                            <Link
                                to="/cart"
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
                                onClick={() => setIsOpen(false)}
                            >
                                <ShoppingCart className="h-5 w-5" />
                                {cartItemsCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-primary-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                        {cartItemsCount}
                                    </span>
                                )}
                            </Link>
                        </div>
                    </div>

                    {/* Search Bar (collapsible) */}
                    {showSearch && (
                        <div className="mt-2">
                            <SearchBar
                                placeholder="Потърси продукт, категория или марка..."
                                autoFocus={true}
                                onSearch={handleSearchComplete}
                            />
                        </div>
                    )}
                </div>
            </header>

            {/* Mobile Menu Overlay */}
            {isOpen && (
                <div className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setIsOpen(false)} />
            )}

            {/* Mobile Menu Sidebar */}
            <div className={`lg:hidden fixed top-0 left-0 h-full w-80 bg-white z-50 transform transition-transform duration-300 ${
                isOpen ? 'translate-x-0' : '-translate-x-full'
            }`}>
                <div className="flex flex-col h-full">
                    {/* Menu Header */}
                    <div className="flex items-center justify-between p-4 border-b">
                        <h2 className="text-lg font-semibold">Меню</h2>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Menu Content */}
                    <div className="flex-1 overflow-y-auto">
                        {/* User Section */}
                        <div className="border-b">
                            <Link
                                to="/profile"
                                className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors"
                                onClick={() => setIsOpen(false)}
                            >
                                <User className="mr-3 h-5 w-5 text-gray-600" />
                                <span className="font-medium">
                                    {isAuthenticated && user ? user.firstName : 'Профил'}
                                </span>
                            </Link>
                        </div>

                        {/* Contact */}
                        <div className="border-b">
                            <a
                                href="tel:+359877769575"
                                className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors"
                            >
                                <Phone className="mr-3 h-5 w-5 text-primary-600" />
                                <span className="font-medium text-primary-600">+359 877 769 575</span>
                            </a>
                        </div>

                        {/* Main Navigation */}
                        <div className="py-2">
                            {/* PC Configurations */}
                            <Link
                                to="/konfiguratsii"
                                className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors"
                                onClick={() => setIsOpen(false)}
                            >
                                <Computer className="mr-3 h-5 w-5 text-gray-600" />
                                <span className="font-medium">PC Конфигурации</span>
                            </Link>

                            {/* Services */}
                            <Link
                                to="/services"
                                className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors"
                                onClick={() => setIsOpen(false)}
                            >
                                <Settings className="mr-3 h-5 w-5 text-gray-600" />
                                <span className="font-medium">Услуги</span>
                            </Link>

                            {/* Admin Panel */}
                            {isAuthenticated && user?.isAdmin && (
                                <Link
                                    to="/admin"
                                    className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <ShoppingBag className="mr-3 h-5 w-5 text-gray-600" />
                                    <span className="font-medium">Админ панел</span>
                                </Link>
                            )}
                        </div>

                        {/* Categories */}
                        <div className="border-t pt-2">
                            <div className="px-4 py-2">
                                <h3 className="text-sm font-semibold text-gray-500 uppercase">Категории</h3>
                            </div>
                            {parentCategories.map(parent => {
                                const childCategories = getChildCategories(parent.id);
                                const isExpanded = expandedCategories.has(parent.id);
                                
                                return (
                                    <div key={parent.id}>
                                        <div className="flex items-center">
                                            {childCategories.length > 0 ? (
                                                // Parent categories with children are only expandable, not clickable
                                                <>
                                                    <button
                                                        onClick={() => toggleCategory(parent.id)}
                                                        className="flex-1 flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors font-medium text-left"
                                                    >
                                                        <span>{getBgName(parent.name)}</span>
                                                        <ChevronRight className={`h-4 w-4 transition-transform ${
                                                            isExpanded ? 'rotate-90' : ''
                                                        }`} />
                                                    </button>
                                                </>
                                            ) : (
                                                // Parent categories without children are clickable
                                                <Link
                                                    to={`/category/${getCategorySlugOrId(parent)}`}
                                                    className="flex-1 px-4 py-3 hover:bg-gray-50 transition-colors font-medium"
                                                    onClick={() => setIsOpen(false)}
                                                >
                                                    {getBgName(parent.name)}
                                                </Link>
                                            )}
                                        </div>
                                        
                                        {isExpanded && childCategories.length > 0 && (
                                            <div className="bg-gray-50">
                                                {childCategories.map(child => (
                                                    <Link
                                                        key={child.id}
                                                        to={`/category/${getCategorySlugOrId(child)}`}
                                                        className="block pl-8 pr-4 py-2 text-sm hover:bg-gray-100 transition-colors"
                                                        onClick={() => setIsOpen(false)}
                                                    >
                                                        {getBgName(child.name)}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Cart Summary */}
                    <div className="border-t p-4">
                        <Link
                            to="/cart"
                            className="block w-full bg-primary-500 text-white text-center py-3 rounded-lg hover:bg-primary-600 transition-colors"
                            onClick={() => setIsOpen(false)}
                        >
                            Количка ({cartItemsCount}) - {formattedTotal}
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MobileNavigation;